/**
 * Active-party persistence listener middleware.
 *
 * Spec: active-party-top-bar — Requirement 5 (per-user persistence).
 *
 * Whenever the user changes the active buyer or supplier (`setActiveBuyer` /
 * `setActiveSupplier`), this listener:
 *   1. mirrors the (buyer, supplier) pair — with display fields — to the
 *      `sm_active_party` local-storage map, keyed by tenant (Req 5.3); and
 *   2. persists the pair to the authoritative typed `UserPreference`
 *      columns `ActiveBuyerPartyId` / `ActiveSupplierPartyId` (Req 5.1).
 *
 * The pair is always written together — `setActiveBuyer` clears the supplier
 * in the reducer, so the post-action state already holds the correct pair, and
 * the backend "always update" path never strands a half-updated pair.
 *
 * Restore-on-load does NOT route through here: the resolution runs inside the
 * `fetchMyBuyers` / `fetchSuppliersForBuyer` fulfilled reducers (it sets state
 * directly, not via these actions), so this listener fires only on genuine
 * user picks — no echo, no idempotent guard needed.
 *
 * Failure handling: a failed DB write is logged only — it never throws or
 * reverts Redux state; the in-memory selection stands (Req 5.5). The next
 * session's restore recovers from the local-storage mirror.
 *
 * Implementation note: matches on action-type strings and calls the service
 * directly (NOT via thunk dispatch), mirroring `tenantPersistListener` — keeps
 * the middleware free of slice imports.
 */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { TypedStartListening } from '@reduxjs/toolkit';
import { userPreferenceService } from '../../services/userPreferenceService';
import { writeActiveParty } from '../slices/activePartyStorage';
import type { RootState, AppDispatch } from '../index';

const SET_ACTIVE_BUYER_ACTION_TYPE = 'activeParty/setActiveBuyer';
const SET_ACTIVE_SUPPLIER_ACTION_TYPE = 'activeParty/setActiveSupplier';

// Take-latest serializer for the DB persist. Switching the active party dispatches BOTH
// `setActiveBuyer` (clears supplier) and `setActiveSupplier` in quick succession, so two effects
// fire near-simultaneously. Firing both PUTs concurrently raced on the single UserPreference row's
// rowversion (EF optimistic concurrency → "expected to affect 1 row, affected 0"). We keep one PUT
// in flight and coalesce to the newest payload — last write wins, which is the intended semantics.
interface ActiveContextPersist {
  userId: number;
  payload: { activeTenantId: number; activeBuyerPartyId: number | null; activeSupplierPartyId: number | null };
}
let isPersistInFlight = false;
let persistQueued: ActiveContextPersist | null = null;

async function flushActivePartyPersist(): Promise<void> {
  if (isPersistInFlight || persistQueued === null) return;
  isPersistInFlight = true;
  const { userId, payload } = persistQueued;
  persistQueued = null;
  try {
    await userPreferenceService.updateMultiplePreferences(userId, payload);
  } catch (err) {
    // Log only — never break the dispatch. The local-storage mirror plus the next preference
    // write recover the desync.
    console.warn('[activePartyPersistListener] Failed to persist active party to user preferences', err);
  } finally {
    isPersistInFlight = false;
    if (persistQueued !== null) void flushActivePartyPersist();
  }
}

export const activePartyPersistListener = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;
const startAppListening = activePartyPersistListener.startListening as AppStartListening;

startAppListening({
  predicate: (action) => {
    const type = (action as { type?: string }).type;
    return (
      type === SET_ACTIVE_BUYER_ACTION_TYPE || type === SET_ACTIVE_SUPPLIER_ACTION_TYPE
    );
  },
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState();

    // No authenticated user — happens during logout bootstrap or in test stores.
    const userIdRaw = state.auth.user?.id;
    if (!userIdRaw) return;
    const userId = Number(userIdRaw);
    if (!Number.isFinite(userId) || userId <= 0) return;

    // Party ids are tenant-scoped — without an active tenant there is nothing
    // meaningful to key the local-storage mirror by.
    const tenantIdRaw = state.tenant.activeTenant?.tenantId;
    if (!tenantIdRaw) return;
    const tenantId = Number(tenantIdRaw);
    if (!Number.isFinite(tenantId) || tenantId <= 0) return;

    const buyer = state.activeParty.activeBuyer;
    const supplier = state.activeParty.activeSupplier;

    // 1. Local-storage mirror — instant render with names next session (Req 5.3).
    writeActiveParty(tenantId, {
      buyer: buyer
        ? { partyId: buyer.partyId, partyCode: buyer.partyCode, partyName: buyer.partyName }
        : null,
      supplier: supplier
        ? { partyId: supplier.partyId, partyCode: supplier.partyCode, partyName: supplier.partyName }
        : null,
    });

    // 2. Authoritative DB persistence — non-blocking (Req 5.1, 5.5), serialized take-latest so the
    // buyer+supplier dispatch pair can't fire overlapping PUTs that race on the row's rowversion.
    // The active-context triple (tenant, buyer, supplier) is written as a group on the backend
    // (gated by UpdateActiveContext), so we always include the current activeTenantId — otherwise
    // its absence in the JSON payload would null the tenant column on every buyer/supplier change.
    persistQueued = {
      userId,
      payload: {
        activeTenantId: tenantId,
        activeBuyerPartyId: buyer?.partyId ?? null,
        activeSupplierPartyId: supplier?.partyId ?? null,
      },
    };
    void flushActivePartyPersist();
  },
});
